import type { EvmChainId } from 'sushi/evm'

export enum PriceWorkerPostMessageType {
  Initialize = 'Initialize',
  IncrementChainId = 'IncrementChainId',
  DecrementChainId = 'DecrementChainId',
  RefetchChainId = 'RefetchChainId',
  SetEnabled = 'SetEnabled',
}

type Initialize = {
  type: PriceWorkerPostMessageType.Initialize
  canUseSharedArrayBuffer: boolean
}

type IncrementChainId<TChainId extends EvmChainId> = {
  chainId: TChainId
  type: PriceWorkerPostMessageType.IncrementChainId
}

type DecrementChainId<TChainId extends EvmChainId> = {
  chainId: TChainId
  type: PriceWorkerPostMessageType.DecrementChainId
}

type RefetchChainId<TChainId extends EvmChainId> = {
  chainId: TChainId
  type: PriceWorkerPostMessageType.RefetchChainId
}

type SetEnabled = {
  enabled: boolean
  type: PriceWorkerPostMessageType.SetEnabled
}

export type PriceWorkerPostMessage<TChainId extends EvmChainId> =
  | Initialize
  | IncrementChainId<TChainId>
  | DecrementChainId<TChainId>
  | RefetchChainId<TChainId>
  | SetEnabled

export enum PriceWorkerReceiveMessageType {
  ChainState = 'ChainState',
}

export interface WorkerChainState<TChainId extends EvmChainId> {
  chainId: TChainId
  listenerCount: number

  priceMap: Map<bigint, number>

  lastModified: number

  isLoading: boolean
  isUpdating: boolean
  isError: boolean
}

export type PriceWorkerReceiveMessageChainState<TChainId extends EvmChainId> = {
  type: PriceWorkerReceiveMessageType.ChainState
  payload: Partial<
    Omit<WorkerChainState<TChainId>, 'priceObject' | 'listenerCount'>
  > & {
    chainId: TChainId
  }
}

export type PriceWorkerReceiveMessage<TChainId extends EvmChainId> =
  PriceWorkerReceiveMessageChainState<TChainId>

export type PriceWorker<TChainId extends EvmChainId> =
  (typeof Worker)['prototype'] & {
    postMessage(
      message:
        | PriceWorkerPostMessage<TChainId>
        | PriceWorkerPostMessage<TChainId>[],
    ): void
  }
